/**
 * Shared constants for canvas tile mini-tiles
 * These dimensions are used for both display and editor components
 */
export const CANVAS_MINI_TILE = {
  WIDTH: 110,
} as const;

export const DEFAULT_TILE_COLOR = '#4370D9';

export const CANVAS_SNAP_GRID = {
  RATIO: 0.01,
  MAX_NUDGE_CELLS: 2,
  LINE_COLOR: DEFAULT_TILE_COLOR,
  LINE_WEIGHT: 1,
  LINE_OPACITY: 0.25,
} as const;

export type AlarmBadgeStage = 'unacked' | 'acked' | null;

// Unacknowledged takes priority over acknowledged, matching the folder-tile badge behavior.
export function aggregateAlarmBadgeStage(stages: AlarmBadgeStage[]): AlarmBadgeStage {
  if (stages.includes('unacked')) return 'unacked';
  if (stages.includes('acked')) return 'acked';
  return null;
}

/**
 * Sizing constants for the Leaflet markercluster badge icon.
 * - OUTER_SIZE_LARGE / OUTER_SIZE_SMALL: outer circle diameter in px
 * - COUNT_THRESHOLD: number of child tiles that triggers the large size
 * - RING_THICKNESS: width of the conic-gradient ring (outer - inner)
 * - FONT_SIZE_LARGE / FONT_SIZE_SMALL: count label font sizes in px
 */
export const CANVAS_CLUSTER_ICON = {
  OUTER_SIZE_LARGE: 48,
  OUTER_SIZE_SMALL: 32,
  COUNT_THRESHOLD: 4,
  RING_THICKNESS: 8,
  FONT_SIZE_LARGE: 20,
  FONT_SIZE_SMALL: 16,
} as const;

/**
 * Maps tile color keys to CSS hex values for cluster icon rendering.
 * Used by iconCreateFunction which generates plain HTML outside Angular.
 *
 * Values are derived from the $tileBackground of each color variant in the
 * tile-data-attr-colors() mixin (src/app/style/_tiles.scss). For gradient
 * backgrounds the dominant (start) color is used; for white/dark backgrounds
 * the literal color is used.
 */
export const TILE_COLOR_CSS: Record<string, string> = {
  '': DEFAULT_TILE_COLOR,       // gradient indigo background
  'indigo': '#4370D9',          // gradient indigo background
  'indigo-white': 'white',      // white background
  'indigo-black': '#222628',    // dark background
  'alt': '#385494',             // gradient indigo-dark background
  'alt-white': 'white',         // white background
  'green': '#9bbf61',           // gradient green background
  'green-white': 'white',       // white background
  'green-black': '#222628',     // dark background
  'black-white': 'white',       // white background
  'yellow': '#ffc050',          // gradient yellow background
  'red': '#f5365c',             // gradient red background
};

/**
 * Custom step option identifiers
 */
export const CUSTOM_STEP_ID = 'custom';
export const CUSTOM_STEP_LABEL = 'Custom';

/**
 * Datetime mode for canvas timeline
 */
export enum DateTimeMode {
  Latest = 'latest',
  Custom = 'custom',
}

/**
 * Display labels for datetime modes
 */
export const DATETIME_MODE_LABELS: Record<DateTimeMode, string> = {
  [DateTimeMode.Latest]: 'Latest',
  [DateTimeMode.Custom]: 'Custom',
};

/**
 * Time step intervals for canvas timeline navigation
 */
export interface TimeStep {
  id: string;
  label: string;
  seconds: number;
}

/**
 * Available time units for custom step configuration
 */
export enum TimeUnit {
  SECONDS = 'seconds',
  MINUTES = 'minutes',
  HOURS = 'hours',
}

/**
 * Time unit conversion multipliers to seconds
 */
export const TIME_UNIT_MULTIPLIERS: Record<TimeUnit, number> = {
  [TimeUnit.SECONDS]: 1,
  [TimeUnit.MINUTES]: 60,
  [TimeUnit.HOURS]: 3600,
} as const;

export const TIME_UNIT_SUFFIX: Record<TimeUnit, string> = {
  [TimeUnit.SECONDS]: 's',
  [TimeUnit.MINUTES]: 'm',
  [TimeUnit.HOURS]: 'h',
};

export const TIME_STEPS: TimeStep[] = [
  { id: '1s', label: '1 Second', seconds: 1 },
  { id: '5s', label: '5 Seconds', seconds: 5 },
  { id: '10s', label: '10 Seconds', seconds: 10 },
  { id: '30s', label: '30 Seconds', seconds: 30 },
  { id: '1m', label: '1 Minute', seconds: 60 },
  { id: '5m', label: '5 Minutes', seconds: 300 },
  { id: '15m', label: '15 Minutes', seconds: 900 },
  { id: '30m', label: '30 Minutes', seconds: 1800 },
  { id: '45m', label: '45 Minutes', seconds: 2700 },
  { id: '1h', label: '1 Hour', seconds: 3600 },
  { id: CUSTOM_STEP_ID, label: CUSTOM_STEP_LABEL, seconds: 0 },
];

export const DEFAULT_TIME_STEP: TimeStep = TIME_STEPS.find(step => step.seconds === TIME_UNIT_MULTIPLIERS[TimeUnit.MINUTES])!;
